/**
 * Sidebar layout. `rail` takes 260px of layout width; `drawer` is the same column fixed over the
 * page on a scrim; `auto` (default) is the rail from 1024px up and the drawer below.
 */
export type EsnSidebarMode = 'auto' | 'rail' | 'drawer';

/**
 * Viewport width below which an `auto` sidebar turns into the drawer (Figma: "Below 1024px").
 */
export const ESN_SIDEBAR_DRAWER_QUERY = '(max-width: 1023.98px)';

/**
 * Empty State flavour: nothing exists yet, or the filter is hiding every row.
 */
export type EsnEmptyStateVariant = 'empty' | 'no-results';

/**
 * Background Glow state; `auto` follows the theme tokens (`[data-theme]`).
 */
export type EsnBackgroundGlowState = 'auto' | 'dark' | 'light';

/**
 * One cell of the pagination row: a page number, or a gap standing for the hidden pages.
 */
export type EsnPaginationCell = { kind: 'page'; page: number } | { kind: 'gap'; key: string };

/**
 * The cells a pagination row shows: every page when they fit in seven cells, otherwise first,
 * last, the current page with its neighbours, and a gap wherever pages are left out.
 */
export function esnPaginationCells(page: number, pageCount: number, siblings = 1): EsnPaginationCell[] {
  const count = Math.max(0, Math.floor(pageCount));
  if (count === 0) return [];
  const current = Math.min(Math.max(1, Math.floor(page)), count);
  const slots = siblings * 2 + 5;
  const pages = (from: number, to: number): EsnPaginationCell[] =>
    Array.from({ length: to - from + 1 }, (_, i) => ({ kind: 'page', page: from + i }));

  if (count <= slots) return pages(1, count);

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, count);
  const showLeftGap = left > 3;
  const showRightGap = right < count - 2;
  const edge = siblings * 2 + 3;

  if (!showLeftGap) return [...pages(1, edge), { kind: 'gap', key: 'end' }, ...pages(count, count)];
  if (!showRightGap) return [...pages(1, 1), { kind: 'gap', key: 'start' }, ...pages(count - edge + 1, count)];
  return [
    ...pages(1, 1),
    { kind: 'gap', key: 'start' },
    ...pages(left, right),
    { kind: 'gap', key: 'end' },
    ...pages(count, count),
  ];
}
